import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { applyIndustryProfileMappingPackage } from '../../services/industryProfileAdminService';

const groups = [
  {
    key: 'hazards',
    title: 'Farer',
    codeField: 'code',
    catalogueKey: 'hazards',
    mappingKey: 'hazards',
    targetField: 'hazard_id',
    labelField: 'title',
    relevance: ['general', 'elevated', 'conditional', 'lower_priority']
  },
  {
    key: 'protectiveFactors',
    title: 'Beskyttelsesfaktorer',
    codeField: 'code',
    catalogueKey: 'factors',
    mappingKey: 'factors',
    targetField: 'protective_factor_id',
    labelField: 'name',
    relevance: ['general', 'elevated', 'conditional', 'lower_priority']
  },
  {
    key: 'questions',
    title: 'Kartleggingsspørsmål',
    codeField: 'question_key',
    catalogueKey: 'questions',
    mappingKey: 'questions',
    targetField: 'question_id',
    labelField: 'question_text',
    relevance: ['core', 'industry_focused', 'conditional']
  }
];

const DEFAULT_MAPPING_PACKAGE = JSON.stringify({
  hazards: [],
  protectiveFactors: [],
  questions: []
}, null, 2);

const getDraftStorageKey = (versionId) => (
  versionId ? `hms_industry_profile_bulk_mapping_draft_${versionId}` : null
);

const loadMappingDraft = (versionId) => {
  const key = getDraftStorageKey(versionId);
  if (!key) return DEFAULT_MAPPING_PACKAGE;

  try {
    return window.localStorage.getItem(key) ?? DEFAULT_MAPPING_PACKAGE;
  } catch {
    return DEFAULT_MAPPING_PACKAGE;
  }
};

const saveMappingDraft = (versionId, value) => {
  const key = getDraftStorageKey(versionId);
  if (!key) return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Keep the editor usable if browser storage is unavailable.
  }
};

const statusLabel = {
  ready: 'Klar',
  linked: 'Allerede koblet',
  missing: 'Mangler i katalog',
  relevance: 'Ugyldig relevans',
  rationale: 'Mangler begrunnelse'
};

const statusStyle = {
  ready: 'bg-emerald-100 text-emerald-800',
  linked: 'bg-amber-100 text-amber-900',
  missing: 'bg-rose-100 text-rose-800',
  relevance: 'bg-orange-100 text-orange-900',
  rationale: 'bg-rose-100 text-rose-800'
};

const displayValue = (value) => typeof value === 'string' ? value : '';

const validateGroup = (group, entries, catalogs, mappings) => {
  const seen = new Set();
  return entries.map((entry) => {
    const code = entry && typeof entry === 'object' ? entry[group.codeField] : '';
    const item = catalogs[group.catalogueKey].find((record) => record[group.codeField] === code);
    let status = 'ready';

    if (!item) {
      status = 'missing';
    } else if (!group.relevance.includes(entry.relevance)) {
      status = 'relevance';
    } else if (typeof entry.rationale !== 'string' || !entry.rationale.trim()) {
      status = 'rationale';
    } else {
      const key = String(item.id);
      const mapped = mappings[group.mappingKey].some((mapping) => mapping[group.targetField] === item.id);
      status = mapped || seen.has(key) ? 'linked' : 'ready';
      seen.add(key);
    }

    return { entry, code, item, status };
  });
};

const summarize = (entries) => ({
  total: entries.length,
  ready: entries.filter((entry) => entry.status === 'ready').length,
  linked: entries.filter((entry) => entry.status === 'linked').length,
  errors: entries.filter((entry) => !['ready', 'linked'].includes(entry.status)).length
});

function PreviewTable({ group, entries }) {
  return (
    <section className="space-y-3">
      <h3 className="font-bold text-slate-900">{group.title}</h3>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full table-fixed text-left text-sm">
          <colgroup>
            <col className="w-[24%]" />
            <col className="w-[34%]" />
            <col className="w-[18%]" />
            <col className="w-[24%]" />
          </colgroup>
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {['Kode / question_key', 'Kataloginnhold', 'Relevans', 'Status'].map((heading) => (
                <th key={heading} className="px-3 py-3 font-semibold">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map(({ entry, code, item, status }, index) => (
              <React.Fragment key={`${String(code)}-${index}`}>
                <tr className="align-top">
                  <td className="px-3 py-3 font-mono text-xs">{displayValue(code) || '—'}</td>
                  <td className="max-w-xs whitespace-pre-wrap px-3 py-3">{item?.[group.labelField] || '—'}</td>
                  <td className="px-3 py-3">{displayValue(entry?.relevance) || '—'}</td>
                  <td className="px-3 py-3"><span className={`inline-flex max-w-full whitespace-normal rounded-full px-2.5 py-1 text-xs font-semibold leading-4 ${statusStyle[status]}`}>{statusLabel[status]}</span></td>
                </tr>
                <tr className="bg-slate-50/70">
                  <td colSpan="4" className="px-3 py-3">
                    <table className="w-full table-fixed text-left text-xs">
                      <colgroup>
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                      </colgroup>
                      <thead className="text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Begrunnelse</th>
                          <th className="px-3 py-2 font-semibold">Vilkår</th>
                          <th className="px-3 py-2 font-semibold">Kilde</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="max-w-xs whitespace-pre-wrap px-3 py-3">{displayValue(entry?.rationale) || '—'}</td>
                          <td className="max-w-xs whitespace-pre-wrap px-3 py-3">{displayValue(entry?.applicability_condition) || '—'}</td>
                          <td className="max-w-xs whitespace-pre-wrap px-3 py-3">{displayValue(entry?.source_reference) || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan="4" className="px-3 py-4 text-slate-500">Ingen oppføringer i pakken.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const executionGroups = [
  { type: 'hazard', title: 'Farer' },
  { type: 'protective_factor', title: 'Beskyttelsesfaktorer' },
  { type: 'question', title: 'Kartleggingsspørsmål' }
];

const countExecution = (results, status) => results.filter(
  (result) => result.status === status
).length;

function ExecutionResults({ results, refreshError }) {
  const failures = results.filter((result) => result.status === 'Feilet');

  return (
    <section className="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
      <h3 className="font-bold text-emerald-900">Bulk mapping fullført</h3>
      {executionGroups.map((group) => {
        const entries = results.filter((result) => result.type === group.type);
        return (
          <div key={group.type}>
            <h4 className="font-semibold text-slate-800">{group.title}</h4>
            <p className="mt-1 text-sm text-slate-700">
              {countExecution(entries, 'Opprettet')} opprettet · {countExecution(entries, 'Allerede koblet')} allerede koblet · {countExecution(entries, 'Feilet')} feilet
            </p>
          </div>
        );
      })}
      {failures.length > 0 && (
        <div className="space-y-2 rounded-lg border border-rose-200 bg-white p-3">
          <h4 className="font-semibold text-rose-800">Feil per oppføring</h4>
          {failures.map((result, index) => (
            <p key={`${result.type}-${result.code || result.question_key}-${index}`} className="break-words text-sm text-rose-700">
              <span className="font-mono">{result.code || result.question_key || 'Ukjent nøkkel'}</span>: {result.error}
            </p>
          ))}
        </div>
      )}
      {refreshError && (
        <p role="alert" className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-800">
          Mappingene ble kjørt, men kunne ikke lastes inn på nytt: {refreshError}
        </p>
      )}
    </section>
  );
}

export default function IndustryProfileBulkMapping({
  catalogs,
  mappings,
  version,
  onApplied
}) {
  const versionId = version?.id ? String(version.id) : '';
  const [draft, setDraft] = useState(() => ({
    versionId,
    value: loadMappingDraft(versionId)
  }));
  const json = draft.versionId === versionId
    ? draft.value
    : loadMappingDraft(versionId);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [executionResults, setExecutionResults] = useState(null);
  const [refreshError, setRefreshError] = useState('');

  useEffect(() => {
    setDraft({
      versionId,
      value: loadMappingDraft(versionId)
    });
    setError('');
    setPreview(null);
    setConfirming(false);
    setExecutionResults(null);
    setRefreshError('');
  }, [versionId]);

  const validate = useCallback(({
    showErrors = true,
    clearExecutionResult = true
  } = {}) => {
    if (showErrors) {
      setError('');
      setRefreshError('');
    }
    if (clearExecutionResult) setExecutionResults(null);

    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch {
      setPreview(null);
      if (showErrors) setError('Ugyldig JSON. Kontroller syntaksen i mappingpakken.');
      return false;
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) ||
      groups.some((group) => !Array.isArray(parsed[group.key]))) {
      setPreview(null);
      if (showErrors) {
        setError('Mappingpakken må være et objekt med hazards, protectiveFactors og questions som lister.');
      }
      return false;
    }

    const catalogsReady = groups.every((group) =>
      Array.isArray(catalogs?.[group.catalogueKey])
    );
    const mappingsReady = groups.every((group) =>
      Array.isArray(mappings?.[group.mappingKey])
    );

    if (!catalogsReady || !mappingsReady) return false;

    setPreview(Object.fromEntries(groups.map((group) => [
      group.key,
      validateGroup(group, parsed[group.key], catalogs, mappings)
    ])));
    setError('');
    return true;
  }, [json, catalogs, mappings]);

  const dataReady = Boolean(
    versionId &&
    draft.versionId === versionId &&
    groups.every((group) => Array.isArray(catalogs?.[group.catalogueKey])) &&
    groups.every((group) => Array.isArray(mappings?.[group.mappingKey]))
  );

  useEffect(() => {
    if (!dataReady) return;
    validate({ showErrors: false, clearExecutionResult: false });
  }, [dataReady, validate]);

  const totals = preview
    ? Object.fromEntries(groups.map((group) => [
      group.key,
      summarize(preview[group.key])
    ]))
    : null;
  const readyCount = totals
    ? groups.reduce((count, group) => count + totals[group.key].ready, 0)
    : 0;
  const hasValidationErrors = totals
    ? groups.some((group) => totals[group.key].errors > 0)
    : true;
  const canApply = Boolean(
    preview &&
    version?.status === 'draft' &&
    readyCount > 0 &&
    !hasValidationErrors &&
    !applying
  );

  const prepareEntries = () => Object.fromEntries(groups.map((group) => [
    group.key,
    preview[group.key].map(({ entry, code, item, status }) => ({
      ...entry,
      [group.codeField]: code,
      targetId: item?.id ?? null,
      status: statusLabel[status]
    }))
  ]));

  const confirmApply = async () => {
    if (!canApply || applying) return;
    setConfirming(false);
    setApplying(true);
    setExecutionResults(null);
    setRefreshError('');

    try {
      const results = await applyIndustryProfileMappingPackage(
        version.id,
        prepareEntries()
      );
      setExecutionResults(results);

      try {
        const refreshedMappings = await onApplied();
        if (refreshedMappings) {
          const parsed = JSON.parse(json);
          setPreview(Object.fromEntries(groups.map((group) => [
            group.key,
            validateGroup(group, parsed[group.key], catalogs, refreshedMappings)
          ])));
        }
      } catch (failure) {
        setRefreshError(failure?.message || 'Ukjent feil ved oppdatering av mappinger.');
      }
    } catch (failure) {
      setError(failure?.message || 'Bulk mapping kunne ikke fullføres.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
      <div>
        <h2 className="font-bold text-slate-900">Bulk mapping</h2>
        <p className="mt-1 text-sm text-slate-500">Forhåndsvis og valider en mappingpakke før den anvendes på bransjeprofilen.</p>
      </div>

      <label className="grid gap-2 text-sm font-semibold text-slate-700">
        Mappingpakke (JSON)
        <textarea
          rows="12"
          value={json}
          disabled={applying}
          onChange={(event) => {
            const value = event.target.value;
            saveMappingDraft(versionId, value);
            setDraft({ versionId, value });
            setPreview(null);
            setExecutionResults(null);
            setError('');
            setRefreshError('');
          }}
          placeholder={'{\n  "hazards": [],\n  "protectiveFactors": [],\n  "questions": []\n}'}
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 font-mono text-xs outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={() => validate()} disabled={applying}>Valider mappingpakke</Button>
        <Button
          type="button"
          disabled={!canApply}
          onClick={() => setConfirming(true)}
        >
          {applying ? 'Mapping pågår...' : 'Bruk mappingpakke'}
        </Button>
        <span className="text-xs text-slate-500">Mappingpakken må være gyldig før den kan anvendes.</span>
      </div>

      {error && <p role="alert" className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}

      {confirming && totals && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-mapping-confirm-title"
          className="rounded-xl border border-blue-200 bg-blue-50 p-5"
        >
          <h3 id="bulk-mapping-confirm-title" className="font-bold text-slate-900">
            Du er i ferd med å legge til følgende koblinger:
          </h3>
          <ul className="mt-3 space-y-1 text-sm text-slate-700">
            <li>{totals.hazards.ready} farer</li>
            <li>{totals.protectiveFactors.ready} beskyttelsesfaktorer</li>
            <li>{totals.questions.ready} kartleggingsspørsmål</li>
          </ul>
          <p className="mt-3 text-sm text-slate-600">
            Hver kobling lagres separat gjennom eksisterende Admin API og registreres i audit trail. Operasjonen er ikke atomisk.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button type="button" variant="secondary" disabled={applying} onClick={() => setConfirming(false)}>
              Avbryt
            </Button>
            <Button type="button" disabled={!canApply || applying} onClick={confirmApply}>
              Bekreft og bruk mappingpakke
            </Button>
          </div>
        </div>
      )}

      {executionResults && (
        <ExecutionResults results={executionResults} refreshError={refreshError} />
      )}

      {preview && (
        <div className="space-y-6">
          {groups.map((group) => {
            const summary = summarize(preview[group.key]);
            return (
              <div key={group.key} className="space-y-3">
                <h3 className="font-semibold text-slate-800">{group.title}: {summary.total} totalt · {summary.ready} klare · {summary.linked} allerede koblet · {summary.errors} feil</h3>
                <PreviewTable group={group} entries={preview[group.key]} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}